// «Verificar certificado de bench» — ficha pública por código de verificación,
// ruta /verificar-bench/:codigo. Es el destino del QR impreso en el PDF del
// certificado (taller.verificar_bench, script 955). Muestra las pruebas de
// banco realizadas, SIN datos del dueño (LOPDP: el RPC ya quita 'cliente').
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BadgeCheck, CalendarClock, Cpu, ExternalLink, Fingerprint,
  Gamepad2, Loader2, ShieldAlert, ShieldCheck, ThermometerSun, Wrench,
} from 'lucide-react';
import { getErrorMessage } from '@/lib/api/client';
import { cn } from '@/lib/utils';
import { tallerRpc, fmtFecha, fmtFechaHora, urlPasaporte, TIPOS_EQUIPO } from './shared';

// ── Contrato de taller.verificar_bench(_codigo) ─────────────────────────────
interface PlantillaItem { clave: string; label: string; unidad?: string; tipo: 'numero' | 'texto' | 'si_no' | 'antes_despues'; menor_mejor?: boolean }
interface VerificarPieza { descripcion: string; original: boolean; serial: string | null }
interface VerificarTaller { nombre: string; razon_social: string; ruc: string; direccion: string | null; telefono: string | null; email: string | null }
interface VerificarEquipo { tipo: string; marca: string | null; modelo: string | null; serial: string | null; pasaporte: string | null }
interface VerificarResp {
  orden: string; fecha: string; garantia_hasta: string | null; trabajo: string | null;
  taller: VerificarTaller | null; equipo: VerificarEquipo; tecnico: string | null;
  plantilla: PlantillaItem[]; pruebas: Record<string, unknown>; piezas: VerificarPieza[];
  codigo: string; estado: 'firmado' | 'sin_firma'; firmado: boolean; emitido_at: string | null;
}

const tipoLabel = (t: string) => TIPOS_EQUIPO.find(([v]) => v === t)?.[1] ?? t;
const num = (v: unknown): number | null => (v === null || v === undefined || v === '') ? null : Number(v);
const txt = (v: unknown): string | null => (v === null || v === undefined || v === '') ? null : String(v);
const esSiNo = (v: unknown): boolean | null => (v === null || v === undefined || v === '') ? null : v === true || v === 'true' || v === 't';

function Seccion({ icono, titulo, children, acento }: { icono: React.ReactNode; titulo: string; children: React.ReactNode; acento?: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-sm">
      <h2 className={cn('mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide', acento ?? 'text-cyan-300')}>{icono} {titulo}</h2>
      {children}
    </div>
  );
}

function MedicionAntesDespues({ item, pruebas }: { item: PlantillaItem; pruebas: Record<string, unknown> }) {
  const antes = num(pruebas[`${item.clave}_antes`]);
  const despues = num(pruebas[`${item.clave}_despues`]);
  if (antes === null && despues === null) return null;
  const maxVal = Math.max(antes ?? 0, despues ?? 0, 1);
  const pctAntes = antes !== null ? Math.max(4, (antes / maxVal) * 100) : 0;
  const pctDespues = despues !== null ? Math.max(4, (despues / maxVal) * 100) : 0;
  let mejora: number | null = null;
  if (antes !== null && despues !== null && antes !== 0) {
    mejora = item.menor_mejor ? ((antes - despues) / Math.abs(antes)) * 100 : ((despues - antes) / Math.abs(antes)) * 100;
  }
  return (
    <div className="rounded-lg border border-white/10 bg-black/20 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-white/85">{item.label}</p>
        {mejora !== null && (
          <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold', mejora >= 0 ? 'bg-emerald-500/20 text-emerald-300' : 'bg-rose-500/20 text-rose-300')}>
            {mejora >= 0 ? '▲' : '▼'} {Math.abs(mejora).toFixed(0)}% {mejora >= 0 ? 'mejor' : 'peor'}
          </span>
        )}
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="w-16 shrink-0 text-[11px] text-white/40">Antes</span>
          <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-rose-400/70" style={{ width: `${pctAntes}%` }} /></div>
          <span className="w-16 shrink-0 text-right font-mono text-xs text-white/70">{antes !== null ? `${antes}${item.unidad ?? ''}` : '—'}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-16 shrink-0 text-[11px] text-white/40">Después</span>
          <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-emerald-400/80" style={{ width: `${pctDespues}%` }} /></div>
          <span className="w-16 shrink-0 text-right font-mono text-xs text-white/70">{despues !== null ? `${despues}${item.unidad ?? ''}` : '—'}</span>
        </div>
      </div>
    </div>
  );
}

function MedicionFila({ item, pruebas }: { item: PlantillaItem; pruebas: Record<string, unknown> }) {
  if (item.tipo === 'antes_despues') return <MedicionAntesDespues item={item} pruebas={pruebas} />;
  let valor: React.ReactNode = '—';
  if (item.tipo === 'si_no') {
    const v = esSiNo(pruebas[item.clave]);
    valor = v === null ? '—' : (
      <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold', v ? 'bg-emerald-500/20 text-emerald-300' : 'bg-rose-500/20 text-rose-300')}>
        {v ? 'Correcto' : 'Con falla'}
      </span>
    );
  } else if (item.tipo === 'numero') {
    const v = num(pruebas[item.clave]);
    valor = v !== null ? `${v}${item.unidad ?? ''}` : '—';
  } else {
    const v = txt(pruebas[item.clave]);
    valor = v ?? '—';
  }
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/20 px-3 py-2">
      <span className="text-sm text-white/80">{item.label}</span>
      <span className="text-right text-sm font-semibold text-white">{valor}</span>
    </div>
  );
}

export function VerificarBench() {
  const { codigo = '' } = useParams();

  const { data, isLoading, isError, error } = useQuery<VerificarResp | null>({
    queryKey: ['taller-verificar-bench', codigo],
    queryFn: () => tallerRpc<VerificarResp | null>('verificar_bench', { _codigo: codigo }),
    retry: 1,
    enabled: !!codigo,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-violet-950 via-slate-950 to-cyan-950 text-white">
        <Loader2 className="mr-2 h-6 w-6 animate-spin text-fuchsia-400" /> Verificando el certificado…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-gradient-to-br from-violet-950 via-slate-950 to-cyan-950 p-6 text-center text-white">
        <ShieldAlert className="h-10 w-10 text-rose-400" />
        <p className="text-xl font-bold text-rose-300">Certificado no encontrado o no válido</p>
        <p className="max-w-sm text-sm text-white/60">{getErrorMessage(error) || 'El código no corresponde a ningún certificado emitido por este taller.'}</p>
      </div>
    );
  }

  const o = data;
  const nombreEquipo = [o.equipo.marca, o.equipo.modelo].filter(Boolean).join(' ') || tipoLabel(o.equipo.tipo);
  const vigente = !!o.garantia_hasta && new Date(`${o.garantia_hasta}T23:59:59`) >= new Date();

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-950 via-slate-950 to-cyan-950 pb-10 text-white">
      <header className="border-b border-white/10 bg-black/20 px-4 py-5">
        <div className="mx-auto max-w-2xl">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-fuchsia-300"><ThermometerSun className="h-3.5 w-3.5" /> Certificado de bench</p>
          <h1 className="mt-0.5 text-lg font-extrabold">{o.taller?.nombre ?? 'Taller'}</h1>
          <p className="mt-2 flex items-center gap-2 text-2xl font-black"><Gamepad2 className="h-6 w-6 text-cyan-300" /> {nombreEquipo}</p>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-4 px-4 pt-5">
        {/* Sello grande */}
        <div className="flex flex-col items-center gap-2 rounded-xl border border-emerald-400/40 bg-emerald-500/10 px-4 py-5 text-center">
          <ShieldCheck className="h-12 w-12 text-emerald-300" />
          <p className="text-xl font-black text-emerald-200">Certificado auténtico</p>
          {o.firmado ? (
            <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-300"><BadgeCheck className="h-4 w-4" /> Firmado electrónicamente</p>
          ) : (
            <p className="max-w-sm text-xs text-amber-300">Emitido sin firma electrónica: válido por esta verificación en línea.</p>
          )}
          <p className="font-mono text-xs text-white/40">Código {o.codigo}{o.emitido_at ? ` · emitido ${fmtFechaHora(o.emitido_at)}` : ''}</p>
        </div>

        {/* Equipo */}
        <Seccion icono={<Fingerprint className="h-4 w-4" />} titulo="Equipo verificado">
          <div className="flex flex-wrap gap-4 text-sm">
            <div><p className="text-xs text-white/50">Tipo</p><p className="font-semibold">{tipoLabel(o.equipo.tipo)}</p></div>
            <div><p className="text-xs text-white/50">Serie</p><p className="font-mono font-semibold">{o.equipo.serial || '— sin registrar —'}</p></div>
          </div>
          {o.equipo.pasaporte && (
            <a href={urlPasaporte(o.equipo.pasaporte)} target="_blank" rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-300 underline">
              Ver el pasaporte completo de este equipo <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </Seccion>

        {/* Taller y técnico */}
        <Seccion icono={<Wrench className="h-4 w-4" />} titulo="Realizado por">
          <div className="space-y-1 text-sm text-white/80">
            {o.taller && <p>{o.taller.razon_social} {o.taller.ruc ? `· RUC ${o.taller.ruc}` : ''}</p>}
            {o.taller?.direccion && <p className="text-xs text-white/50">{o.taller.direccion}</p>}
            {o.tecnico && <p className="text-xs text-white/50">Técnico: {o.tecnico}</p>}
            <p className="text-xs text-white/50">Orden {o.orden} · {fmtFecha(o.fecha)}</p>
          </div>
        </Seccion>

        {/* Trabajo realizado */}
        {o.trabajo && (
          <Seccion icono={<Cpu className="h-4 w-4" />} titulo="Trabajo realizado">
            <p className="text-sm text-white/80">{o.trabajo}</p>
          </Seccion>
        )}

        {/* Mediciones de bench */}
        {o.plantilla.length > 0 && (
          <Seccion icono={<ThermometerSun className="h-4 w-4" />} titulo="Pruebas de banco">
            <div className="space-y-2">
              {o.plantilla.map((item) => <MedicionFila key={item.clave} item={item} pruebas={o.pruebas} />)}
            </div>
          </Seccion>
        )}

        {/* Piezas */}
        {o.piezas.length > 0 && (
          <Seccion icono={<Cpu className="h-4 w-4" />} titulo="Repuestos instalados">
            <ul className="space-y-1.5">
              {o.piezas.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Cpu className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-300" />
                  <div className="min-w-0 flex-1">
                    <span className="text-white/85">{p.descripcion}</span>
                    <span className={cn('ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold', p.original ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300')}>
                      {p.original ? 'Original' : 'Alternativo'}
                    </span>
                    {p.serial && <span className="ml-1.5 text-[10px] text-white/40">S/N {p.serial}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </Seccion>
        )}

        {/* Garantía */}
        <div className={cn('flex items-center gap-3 rounded-xl border px-4 py-3', vigente ? 'border-emerald-400/40 bg-emerald-500/10' : 'border-white/15 bg-white/[0.03]')}>
          {vigente ? <ShieldCheck className="h-7 w-7 shrink-0 text-emerald-300" /> : <ShieldAlert className="h-7 w-7 shrink-0 text-white/40" />}
          <p className={cn('text-sm font-semibold', vigente ? 'text-emerald-200' : 'text-white/70')}>
            {vigente ? `Garantía vigente hasta ${fmtFecha(o.garantia_hasta)}` : 'Sin garantía vigente'}
          </p>
        </div>

        <p className="flex items-center justify-center gap-1.5 pt-2 text-center text-[11px] text-white/30"><CalendarClock className="h-3 w-3" /> Verificación pública del certificado — sin datos del dueño del equipo</p>
      </main>
    </div>
  );
}
